import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CloudOfficeResponse } from '../models/shared.models';
import { O365ProductApiResponse } from '../models/o365-product.models';

@Injectable({
  providedIn: 'root'
})
export class SignupAPIService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch Office 365 product catalog.
   * Endpoint: /v2/products/o365
   */
  getO365Products(): Observable<O365ProductApiResponse> {
    return this.http.get<O365ProductApiResponse>(`${this.baseUrl}/v2/products/o365`);
  }

  /**
   * Validate domain for Apps flow.
   * Endpoint: /v2/validation/domain
   */
  validateDomainApps(sld: string, tld: string): Observable<{ domainAvailable: boolean }> {
    return this.http.post<{ domainAvailable: boolean }>(
      `${this.baseUrl}/v2/validation/domain`,
      { sld, tld }
    );
  }

  /**
   * Validate domain for Office365 flow.
   * Endpoint: /v2/validation/office365_domain
   */
  validateDomainOffice365(domainName: string): Observable<{ domainTaken: boolean }> {
    return this.http.post<{ domainTaken: boolean }>(
      `${this.baseUrl}/v2/validation/office365_domain`,
      { domainName }
    );
  }

  /**
   * Validate Cloud username availability.
   * Endpoint: /v2/validation/cloud-username
   */
  validateUsername(username: string): Observable<{ total: number; exist: boolean }> {
    return this.http.get<{ total: number; exist: boolean }>(
      `${this.baseUrl}/v2/validation/cloud-username?username=${encodeURIComponent(username)}`
    );
  }

  /**
   * Validate Cloud Office credentials (username + domain).
   * Endpoint: /v2/validation/cloud_office
   */
  cloudOfficeValidation(
    payload: { username: string; domainName?: string; password: string },
    token: string
  ) {
    return this.http.post<CloudOfficeResponse>(
      `${this.baseUrl}/v2/validation/cloud_office`,
      payload,
      {
        headers: { 'X-R-Token': token }
      }
    );
  }

  /**
   * Create signup.
   * Endpoint: /v2/signups/{type}
   */
  createSignup(payload: any): Observable<any> {
    const type = (payload?.type || '').toLowerCase();
    const endpoint = `${this.baseUrl}/v2/signups/${type}`;
    return this.http.post(endpoint, payload);
  }

  /**
   * Get signup status.
   * Endpoint: /v2/signups/{id}/status
   */
  getSignupStatus(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/v2/signups/${id}/status`);
  }

  /**
   * Start email validation.
   * Endpoint: /v2/email_verifications
   */
  validateEmail(email: string) {
    return this.http.post<{ email: string; sessionId: string; expireInSeconds: number }>(
      `${this.baseUrl}/v2/email_verifications`,
      { email }
    );
  }

  /**
   * Verify email passcode.
   * Endpoint: /v2/email_verifications/{sessionId}/verify
   */
  verifyPasscode(sessionId: string, email: string, verificationCode: string) {
    return this.http.post(`${this.baseUrl}/v2/email_verifications/${sessionId}/verify`, {
      email,
      verificationCode,
    });
  }

  /**
   * Resend verification email.
   * Endpoint: /v2/email_verifications/{sessionId}/resend
   */
  resendEmail(sessionId: string, email: string) {
    return this.http.post(`${this.baseUrl}/v2/email_verifications/${sessionId}/resend`, {
      email
    });
  }
}
